// Chat Thread pane for Inbox v2 (PRD §1.7, WP-04).
// Message pagination (Load more), day dividers, read ticks with tooltip time, WhatsApp call cards,
// internal notes, media attachments with lightbox, message reactions, AI translate on messages, and floating "New message ↓" jump.
import { useState, useRef, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { cn } from '../../lib/cn';
import { useMessages, sendReaction } from '../../lib/inbox';
import { translateMessage } from '../../lib/media';
import { useVirtualList } from '../../lib/useVirtualList';
import type { LiveConversation, InboxMessage, MsgStatus } from '../../lib/inboxTypes';
import ChatHeader from './ChatHeader';
import ChatComposer from './ChatComposer';
import MediaLightbox from './MediaLightbox';
import FollowupModal from './FollowupModal';
import { ChevronDownIcon, PhoneIcon } from '../icons';
import { useToast } from '../ui/Toast';
import { getDeliveryFailureReason } from '../../lib/deliveryError';

const REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

function ReactButton({
  conversationId,
  messageId,
  current,
}: {
  conversationId: string;
  messageId: string;
  current: string | null | undefined;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0 opacity-0 transition group-hover:opacity-100">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="React"
        className="grid h-6 w-6 place-items-center rounded-full text-xs text-slate-400 hover:bg-black/5 dark:hover:bg-white/10"
      >
        😊
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-20 cursor-default"
          />
          <div className="absolute bottom-full z-30 mb-1 flex gap-0.5 rounded-full border border-line bg-white p-1 shadow-lg dark:border-slate-800 dark:bg-slate-900">
            {REACTIONS.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => {
                  void sendReaction({ conversationId, messageId, emoji: e === current ? '' : e }).catch((err) => {
                    toast.error((err as Error).message || t('inbox.reactionFailed'));
                  });
                  setOpen(false);
                }}
                className={cn(
                  'grid h-7 w-7 place-items-center rounded-full text-base transition hover:scale-125',
                  e === current && 'bg-brand-50 dark:bg-brand-950/40',
                )}
              >
                {e}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FailedMessageIndicator({ error }: { error?: InboxMessage['error'] }) {
  const { t } = useI18n();
  const [showDetails, setShowDetails] = useState(false);
  const reason = getDeliveryFailureReason(error, t);
  const rawCode = error?.code ? String(error.code) : null;

  return (
    <div className="relative mt-2 border-t border-rose-300/70 pt-1.5 dark:border-rose-800/80">
      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        onMouseEnter={() => setShowDetails(true)}
        onMouseLeave={() => setShowDetails(false)}
        className="flex items-center gap-1.5 text-[11px] font-bold text-rose-700 dark:text-rose-400 hover:underline cursor-pointer select-none"
        title={reason}
      >
        <span>⚠️</span>
        <span>{t('inbox.failed.notDelivered')}</span>
        <span className="text-[9px] font-normal opacity-75">(ℹ)</span>
      </button>

      {showDetails && (
        <div className="absolute bottom-full start-0 z-30 mb-1 w-64 rounded-xl border border-rose-200 bg-white p-3 text-xs text-rose-950 shadow-xl dark:border-rose-900 dark:bg-slate-900 dark:text-rose-200 leading-relaxed animate-in fade-in">
          <div className="font-bold mb-1 flex items-center justify-between text-[11px]">
            <span>⚠️ {t('inbox.failed.failureReason')}</span>
            {rawCode && <span className="font-mono text-[9px] text-slate-500 bg-slate-100 dark:bg-slate-800 px-1 py-0.5 rounded">{rawCode}</span>}
          </div>
          <p className="text-[11px] text-ink-2 dark:text-slate-300">{reason}</p>
        </div>
      )}
    </div>
  );
}

function StatusTicks({ status, time }: { status: MsgStatus; time: string }) {
  if (status === 'failed') {
    return (
      <span
        title={`Not delivered · ${time}`}
        className="text-[11px] font-bold text-rose-600 dark:text-rose-400 select-none"
        aria-label={`Not delivered · ${time}`}
      >
        ⚠️
      </span>
    );
  }
  const read = status === 'read';
  const delivered = status === 'delivered';
  const ticks = status === 'sent' ? '✓' : '✓✓';
  const tooltip = read ? `Read · ${time}` : delivered ? `Delivered · ${time}` : `Sent · ${time}`;

  return (
    <span
      title={tooltip}
      className={cn('text-[10px] font-bold select-none', read ? 'text-blue-500' : 'text-slate-400')}
      aria-label={tooltip}
    >
      {ticks}
    </span>
  );
}

function MediaAttachmentView({
  message,
  onOpenLightbox,
}: {
  message: InboxMessage;
  onOpenLightbox: (id: string) => void;
}) {
  const url = message.mediaUrl;
  const type = message.mediaType;
  if (!url) return null;

  if (type === 'image' || (!type && /\.(jpeg|jpg|png|webp|gif)/i.test(url))) {
    return (
      <button
        type="button"
        onClick={() => onOpenLightbox(message.id)}
        className="block overflow-hidden rounded-lg cursor-zoom-in transition hover:opacity-95"
      >
        <img src={url} alt="" loading="lazy" className="max-h-64 max-w-full object-cover rounded-lg" />
      </button>
    );
  }

  if (type === 'video' || (!type && /\.(mp4|mov|webm)/i.test(url))) {
    return (
      <div className="relative rounded-lg overflow-hidden">
        <video src={url} controls className="max-h-64 max-w-full rounded-lg" />
      </div>
    );
  }

  if (type === 'audio' || (!type && /\.(mp3|ogg|wav|opus|webm)/i.test(url))) {
    return <audio src={url} controls className="w-full max-w-[240px]" />;
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      download
      className="flex items-center gap-2 rounded-lg bg-black/5 dark:bg-white/10 px-3 py-2 text-xs font-semibold text-primary underline"
    >
      📎 {message.body && message.body !== '[media]' ? message.body : 'Attached Document'}
    </a>
  );
}

interface ChatThreadProps {
  conv: LiveConversation;
  onBack: () => void;
  onInfo: () => void;
}

export default function ChatThread({ conv, onBack, onInfo }: ChatThreadProps) {
  const { t } = useI18n();
  const toast = useToast();
  const { messages, loading, hasMore, loadMore } = useMessages(conv.id, 50);
  const [inThreadSearch, setInThreadSearch] = useState('');
  const [followupOpen, setFollowupOpen] = useState(false);
  const [lightboxMessageId, setLightboxMessageId] = useState<string | null>(null);
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [translatedMap, setTranslatedMap] = useState<Record<string, string>>({});
  const [translatingId, setTranslatingId] = useState<string | null>(null);

  const threadContainerRef = useRef<HTMLDivElement>(null);
  const isAutoScrollingRef = useRef(true);

  const scrollToBottom = () => {
    if (threadContainerRef.current) {
      threadContainerRef.current.scrollTop = threadContainerRef.current.scrollHeight;
    }
    setShowScrollBottom(false);
  };

  useEffect(() => {
    if (isAutoScrollingRef.current) {
      scrollToBottom();
    }
  }, [messages]);

  const handleScroll = () => {
    if (!threadContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = threadContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
    isAutoScrollingRef.current = isAtBottom;
    setShowScrollBottom(!isAtBottom);
  };

  const handleTranslateMessage = async (msg: InboxMessage) => {
    if (translatedMap[msg.id]) {
      // Toggle off
      setTranslatedMap((prev) => {
        const next = { ...prev };
        delete next[msg.id];
        return next;
      });
      return;
    }

    setTranslatingId(msg.id);
    try {
      const isArabic = /[\u0600-\u06FF]/.test(msg.body);
      const targetLanguage = isArabic ? 'English' : 'Arabic';
      const res = await translateMessage({
        conversationId: conv.id,
        messageId: msg.id,
        text: msg.body,
        targetLanguage,
      });
      if (res.ok && res.translated) {
        setTranslatedMap((prev) => ({ ...prev, [msg.id]: res.translated }));
      }
    } catch (err) {
      toast.error((err as Error).message || t('inbox.translateFailed'));
    } finally {
      setTranslatingId(null);
    }
  };

  const filteredMessages = inThreadSearch.trim()
    ? messages.filter((m) =>
        m.body.toLowerCase().includes(inThreadSearch.trim().toLowerCase()),
      )
    : messages;

  const virtual = useVirtualList({
    itemCount: filteredMessages.length,
    containerRef: threadContainerRef,
    enabled: filteredMessages.length > 200,
    itemHeight: 72,
    overscan: 25,
  });

  const visibleMessages = virtual.isVirtual
    ? filteredMessages.slice(virtual.startIndex, virtual.endIndex)
    : filteredMessages;

  function dayLabel(day: string): string {
    if (day === 'today') return t('inbox.day.today') || 'Today';
    if (day === 'yesterday') return t('inbox.day.yesterday') || 'Yesterday';
    return day;
  }

  let lastDay = virtual.isVirtual && virtual.startIndex > 0
    ? (filteredMessages[virtual.startIndex - 1]?.day || '')
    : '';

  return (
    <div className="flex h-full min-h-0 flex-col bg-chat-bg dark:bg-slate-950 transition-colors">
      {/* Chat Header */}
      <ChatHeader
        conv={conv}
        onBack={onBack}
        onInfo={onInfo}
        onOpenFollowup={() => setFollowupOpen(true)}
        inThreadSearch={inThreadSearch}
        onInThreadSearchChange={setInThreadSearch}
      />

      {/* Messages Scroll Area */}
      <div
        ref={threadContainerRef}
        onScroll={handleScroll}
        className="relative flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-4"
      >
        {/* Top spacer for virtualization */}
        {virtual.isVirtual && virtual.topPadding > 0 && (
          <div style={{ height: virtual.topPadding, flexShrink: 0 }} aria-hidden />
        )}

        {/* Load More Button */}
        {hasMore && (
          <div className="flex justify-center pb-2">
            <button
              type="button"
              onClick={loadMore}
              className="rounded-full bg-white/80 px-4 py-1 text-xs font-bold text-slate-700 shadow-sm transition hover:bg-white dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-750"
            >
              {t('inbox.loadEarlier') || 'Load earlier messages'}
            </button>
          </div>
        )}

        {loading && messages.length === 0 && (
          <div className="grid flex-1 place-items-center text-xs text-slate-500">
            {t('common.loading') || 'Loading thread…'}
          </div>
        )}

        {visibleMessages.map((m) => {
          const isNote = m.direction === 'note' || m.type === 'note';
          const isCall = m.type === 'call';
          const isOut = m.direction === 'out';
          const isFailed = isOut && m.status === 'failed';
          const showDay = m.day !== lastDay;
          lastDay = m.day;
          const translated = translatedMap[m.id];

          return (
            <div key={m.id} className="contents">
              {showDay && (
                <div className="my-1.5 flex justify-center">
                  <span className="rounded-full bg-white/80 dark:bg-slate-800/90 px-3 py-0.5 text-[10px] font-bold text-ink-3 dark:text-slate-400 shadow-sm select-none">
                    {dayLabel(m.day)}
                  </span>
                </div>
              )}

              {isCall ? (
                /* WhatsApp Call Event Card */
                <div className="my-1 flex justify-center">
                  <div className="flex items-center gap-2 rounded-xl bg-white/90 dark:bg-slate-850 px-4 py-2 text-xs font-medium text-ink dark:text-slate-200 shadow-sm border border-line dark:border-slate-800">
                    <div className="grid h-6 w-6 place-items-center rounded-full bg-blue-100 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
                      <PhoneIcon size={13} />
                    </div>
                    <span>{m.body || 'WhatsApp Voice Call'}</span>
                    <span className="text-[10px] text-ink-4 dark:text-slate-500">{m.time}</span>
                  </div>
                </div>
              ) : isNote ? (
                /* Internal Note Message Card */
                <div className="my-1 flex justify-center">
                  <div className="max-w-[85%] rounded-xl border border-amber-200 bg-amber-50/95 dark:border-amber-900/50 dark:bg-amber-950/40 p-3 shadow-sm">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-800 dark:text-amber-300 mb-1">
                      <span>📝</span>
                      <span>{t('inbox.ctx.noteHeader') || 'Internal Note'}</span>
                      <span className="text-[10px] font-normal text-amber-700/80 dark:text-amber-400/70 ms-auto">
                        {m.time}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap break-words text-xs text-amber-950 dark:text-amber-100 leading-relaxed">
                      {m.body}
                    </p>
                  </div>
                </div>
              ) : (
                /* Inbound / Outbound Chat Bubble */
                <div
                  className={cn(
                    'group flex items-end gap-1',
                    isOut ? 'justify-end' : 'justify-start',
                  )}
                >
                  {isOut && (
                    <ReactButton conversationId={conv.id} messageId={m.id} current={m.agentReaction} />
                  )}

                  <div
                    className={cn(
                      'relative max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-xs leading-relaxed shadow-sm',
                      isOut
                        ? isFailed
                          ? 'rounded-ee-sm border-2 border-rose-300 bg-rose-50/90 text-rose-950 dark:border-rose-700 dark:bg-rose-950/50 dark:text-rose-100'
                          : 'rounded-ee-sm bg-chat-bubbleOut text-ink dark:bg-chat-bubbleOutDark dark:text-white'
                        : 'rounded-es-sm bg-white text-ink dark:bg-slate-850 dark:text-slate-100',
                    )}
                  >
                    {/* Media preview */}
                    {m.mediaUrl && (
                      <div className="mb-1.5">
                        <MediaAttachmentView
                          message={m}
                          onOpenLightbox={(id) => setLightboxMessageId(id)}
                        />
                      </div>
                    )}

                    {/* Message Body */}
                    {m.body && m.body !== '[media]' && <div>{m.body}</div>}

                    {/* Failed message status indicator */}
                    {isFailed && (
                      <FailedMessageIndicator error={m.error} />
                    )}

                    {/* Inline translation if requested */}
                    {translated && (
                      <div className="mt-2 border-t border-black/10 dark:border-white/10 pt-1.5 text-xs italic text-ink-2 dark:text-slate-200">
                        <span className="text-[10px] font-bold uppercase not-italic text-brand block mb-0.5">
                          🌐 {t('inbox.translated') || 'Translated'}:
                        </span>
                        {translated}
                      </div>
                    )}

                    {/* Metadata footer */}
                    <div className="mt-1 flex items-center justify-end gap-1.5 text-[10px] text-ink-3 dark:text-slate-400 select-none">
                      {/* Translate button */}
                      {m.body && m.body !== '[media]' && (
                        <button
                          type="button"
                          onClick={() => void handleTranslateMessage(m)}
                          title={t('inbox.translateThis') || 'Translate message'}
                          className="opacity-0 group-hover:opacity-100 transition-opacity hover:text-brand"
                        >
                          {translatingId === m.id ? '…' : '🌐'}
                        </button>
                      )}

                      {m.source === 'business_app' && (
                        <span title={t('inbox.source.phone') || 'Sent from phone app'}>📱</span>
                      )}

                      <span>{m.time}</span>

                      {isOut && m.status && <StatusTicks status={m.status} time={m.time} />}
                    </div>

                    {/* Emoji reactions */}
                    {(m.customerReaction || m.agentReaction) && (
                      <div className="mt-1 flex justify-end gap-1 text-xs">
                        {m.customerReaction && (
                          <span
                            className="rounded-full bg-black/5 dark:bg-white/10 px-1.5 py-0.2"
                            title={t('inbox.reaction.customer') || 'Customer reacted'}
                          >
                            {m.customerReaction}
                          </span>
                        )}
                        {m.agentReaction && (
                          <span
                            className="rounded-full bg-black/5 dark:bg-white/10 px-1.5 py-0.2"
                            title={t('inbox.reaction.you') || 'You reacted'}
                          >
                            {m.agentReaction}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {!isOut && (
                    <ReactButton conversationId={conv.id} messageId={m.id} current={m.agentReaction} />
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Bottom spacer for virtualization */}
        {virtual.isVirtual && virtual.bottomPadding > 0 && (
          <div style={{ height: virtual.bottomPadding, flexShrink: 0 }} aria-hidden />
        )}

        {/* Floating "New Message ↓" jump button */}
        {showScrollBottom && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="sticky bottom-2 self-center flex items-center gap-1.5 rounded-full bg-brand px-3.5 py-1.5 text-xs font-bold text-white shadow-lg transition hover:bg-brand-dark animate-bounce"
          >
            <ChevronDownIcon size={14} />
            <span>{t('inbox.newMessageJump') || 'New message'}</span>
          </button>
        )}
      </div>

      {/* Composer Pane */}
      <ChatComposer conv={conv} onMessageSent={scrollToBottom} />

      {/* Media Lightbox */}
      {lightboxMessageId && (
        <MediaLightbox
          messages={messages}
          currentMessageId={lightboxMessageId}
          onClose={() => setLightboxMessageId(null)}
          onNavigate={(id) => setLightboxMessageId(id)}
        />
      )}

      {/* Follow-up Stopwatch Modal */}
      {followupOpen && (
        <FollowupModal
          conv={conv}
          onClose={() => setFollowupOpen(false)}
        />
      )}
    </div>
  );
}
